type QuestionAnswerType = string | File | null;

export interface QuestionType {
    id: QuestionId;
    label: string;
    required: boolean;
    type: "text" | "select" | "dropdown" | "label" | "file";
    displayRule?: {questionId: QuestionId; answer: string};
    displayMultiRules?: {questionId: QuestionId; answer: string}[];
    options?: {label: string; value: string}[];
    answer?: QuestionAnswerType;
}

export type QuestionId =
    | "firstName"
    | "lastName"
    | "nickName"
    | "email"
    | "phone"
    | "zip"
    | "city"
    | "state"
    | "workAuthorization"
    | "relocation"
    | "willRelocate" // for staffAug
    | "codingAbility"
    | "techSelection"
    | "strongestLanguageOther"
    | "yearsOfExperience"
    | "currentlyStudent"
    | "graduationMonth"
    | "graduationYear"
    | "degreeExpected"
    | "highestDegree"
    | "major"
    | "linkedin"
    | "voluntaryDisclosures"
    | "gender"
    | "disability"
    | "race"
    | "isMilitary"
    | "militaryStatus"
    | "militaryBranch"
    | "resume"
    | "yearsOfProfessionalExperience"; // for staffAug;

export const questionList: Record<QuestionId, QuestionType> = {
    firstName: {id: "firstName", label: "First Name", required: true, type: "text", answer: ""},
    lastName: {id: "lastName", label: "Last Name", required: true, type: "text", answer: ""},
    nickName: {id: "nickName", label: "Preferred Name (optional)", required: false, type: "text", answer: ""},
    email: {id: "email", label: "Email", required: true, type: "text", answer: ""},
    phone: {
        id: "phone",
        label: "Mobile Phone",
        required: true,
        type: "text",
        answer: "",
    },
    zip: {
        id: "zip",
        label: "Zip Code",
        required: true,
        type: "text",
        answer: "",
    },
    city: {id: "city", label: "City", required: true, type: "dropdown", answer: ""},
    state: {
        id: "state",
        label: "State",
        required: true,
        type: "dropdown",
        options: [],
        answer: "",
    },
    workAuthorization: {
        id: "workAuthorization",
        label: "Are you legally authorized to work in the US?",
        required: true,
        type: "select",
        options: [],
        answer: "",
    },
    relocation: {
        id: "relocation",
        label: "Willingness to relocate",
        required: true,
        type: "select",
        options: [
            {label: "Absolutely! Up for a new adventure", value: "Yes"},
            {label: "Would consider moving for the right role", value: "Undecided"},
            {label: "Not an option", value: "No"},
        ],
        answer: "",
    },
    willRelocate: {
        id: "willRelocate",
        label: "Willingness to relocate",
        required: true,
        type: "select",
        options: [
            {label: "Absolutely! Up for a new adventure", value: "Yes"},
            {label: "Would consider moving for the right role", value: "Undecided"},
            {label: "Not an option", value: "No"},
        ],
        answer: "",
    },
    codingAbility: {
        id: "codingAbility",
        label: "How would you rate your coding ability?",
        required: true,
        type: "select",
        options: Array.from({length: 11}).map((_, i) => ({label: String(i), value: String(i)})),
        answer: "",
    },
    techSelection: {
        id: "techSelection",
        label: "If you had to choose one, which coding language is your strongest?",
        required: true,
        type: "select",
        options: [
            {label: "Java", value: "java"},
            {label: "Python", value: "python"},
            {label: "C/C++", value: "c"},
            {label: ".NET", value: "dotNet"},
            {label: "Other", value: "other"},
        ],
        answer: "",
    },
    strongestLanguageOther: {
        id: "strongestLanguageOther",
        label: "Enter your strongest coding language",
        displayRule: {questionId: "techSelection", answer: "other"},
        required: true,
        type: "text",
        answer: "",
    },
    yearsOfExperience: {
        id: "yearsOfExperience",
        label: "Years of coding experience (including personal/educational projects)",
        required: true,
        type: "select",
        options: [
            {label: "0-1 years", value: "0-1"},
            {label: "1-2 years", value: "1-2"},
            {label: "2-3 years", value: "2-3"},
            {label: "3+ years", value: "3+"},
        ],
        answer: "",
    },
    currentlyStudent: {
        id: "currentlyStudent",
        label: "Are you currently a student?",
        required: true,
        type: "select",
        options: [
            {label: "Yes", value: "Yes"},
            {label: "No", value: "No"},
        ],
        answer: "",
    },
    graduationMonth: {
        id: "graduationMonth",
        label: "Expected Graduation Month",
        required: true,
        type: "dropdown",
        displayRule: {questionId: "currentlyStudent", answer: "Yes"},
        options: [
            {label: "Expected Graduation Month*", value: ""},
            {label: "January", value: "01"},
            {label: "February", value: "02"},
            {label: "March", value: "03"},
            {label: "April", value: "04"},
            {label: "May", value: "05"},
            {label: "June", value: "06"},
            {label: "July", value: "07"},
            {label: "August", value: "08"},
            {label: "September", value: "09"},
            {label: "October", value: "10"},
            {label: "November", value: "11"},
            {label: "December", value: "12"},
        ],
        answer: "",
    },
    graduationYear: {
        id: "graduationYear",
        label: "Expected Graduation Year",
        required: true,
        type: "select",
        displayRule: {questionId: "currentlyStudent", answer: "Yes"},
        options: Array.from(Array(4)).map((_, i) => {
            const currentYear = new Date().getFullYear();
            const year = String(currentYear + i);
            return {label: year, value: year};
        }),
        answer: "",
    },
    degreeExpected: {
        id: "degreeExpected",
        label: "Degree Expected",
        required: true,
        type: "select",
        displayRule: {questionId: "currentlyStudent", answer: "Yes"},
        options: [
            {label: "High School", value: "High School"},
            {label: "Associate's", value: "Associate's"},
            {label: "Bachelor's", value: "Bachelor's"},
            {label: "Master's", value: "Master's"},
            {label: "PhD", value: "PhD"},
        ],
        answer: "",
    },
    highestDegree: {
        id: "highestDegree",
        label: "Highest Degree Achieved",
        required: true,
        type: "select",
        displayRule: {questionId: "currentlyStudent", answer: "No"},
        options: [
            {label: "None", value: "None"},
            {label: "GED", value: "GED"},
            {label: "High School", value: "High School"},
            {label: "Associate's", value: "Associate's"},
            {label: "Bachelor's", value: "Bachelor's"},
            {label: "Master's", value: "Master's"},
            {label: "PhD", value: "PhD"},
        ],
        answer: "",
    },
    major: {
        id: "major",
        label: "Major",
        required: true,
        type: "text",
        displayMultiRules: [
            {questionId: "highestDegree", answer: "Associate's"},
            {questionId: "highestDegree", answer: "Bachelor's"},
            {questionId: "highestDegree", answer: "Master's"},
            {questionId: "highestDegree", answer: "PhD"},
            {questionId: "degreeExpected", answer: "Associate's"},
            {questionId: "degreeExpected", answer: "Bachelor's"},
            {questionId: "degreeExpected", answer: "Master's"},
            {questionId: "degreeExpected", answer: "PhD"},
        ],
        answer: "",
    },
    linkedin: {id: "linkedin", label: "LinkedIn URL (optional)", required: false, type: "text", answer: ""},
    voluntaryDisclosures: {
        id: "voluntaryDisclosures",
        label: "Voluntary Disclosures",
        required: false,
        type: "select",
        answer: "",
    },
    gender: {
        id: "gender",
        label: "Gender",
        required: true,
        type: "select",
        options: [
            {label: "Male", value: "Male"},
            {label: "Female", value: "Female"},
            {label: "Nonbinary", value: "Nonbinary"},
            {label: "Opt Out", value: "Opt Out"},
        ],
        answer: "",
    },
    disability: {
        id: "disability",
        label: "Disability",
        required: true,
        type: "select",
        options: [
            {label: "No", value: "No"},
            {label: "Yes", value: "Yes"},
            {label: "Opt Out", value: "Opt Out"},
        ],
        answer: "",
    },
    race: {
        id: "race",
        label: "Race/Ethnicity",
        required: true,
        type: "select",
        options: [
            {label: "Hispanic or Latino", value: "Hispanic or Latino"},
            {label: "American Indian", value: "American Indian"},
            {label: "Alaska Native", value: "Alaska Native"},
            {label: "Asian", value: "Asian"},
            {label: "Black or African American", value: "Black or African American"},
            {label: "Native Hawaiian ", value: "Native Hawaiian "},
            {label: "Other Pacific Islander", value: "Other Pacific Islander"},
            {label: "Two or More Races", value: "Two or More Races"},
            {label: "White", value: "White"},
            {label: "Opt Out", value: "Opt Out"},
        ],
        answer: "",
    },
    isMilitary: {
        id: "isMilitary",
        label: "Are you a veteran or currently serving in the military?",
        required: true,
        type: "select",
        options: [
            {label: "Yes", value: "Yes"},
            {label: "No", value: "Civilian"},
            {label: "Do not wish to specify", value: "Undisclosed"},
        ],
        answer: "",
    },
    militaryStatus: {
        id: "militaryStatus",
        label: "Military Status",
        required: true,
        type: "select",
        displayRule: {questionId: "isMilitary", answer: "Yes"},
        options: [
            {label: "Veteran", value: "Veteran"},
            {label: "Active", value: "Active"},
        ],
        answer: "",
    },
    militaryBranch: {
        id: "militaryBranch",
        label: "Military Branch",
        required: true,
        type: "select",
        displayRule: {questionId: "isMilitary", answer: "Yes"},
        options: [
            {label: "Army", value: "Army"},
            {label: "Air Force", value: "Air Force"},
            {label: "Navy", value: "Navy"},
            {label: "Marine Corps", value: "Marine Corps"},
            {label: "Coast Guard", value: "Coast Guard"},
            {label: "Reserves", value: "Reserves"},
            {label: "Other", value: "Other"},
        ],
        answer: "",
    },
    resume: {id: "resume", label: "Upload Resume", required: true, type: "file", answer: null},
    yearsOfProfessionalExperience: {
        id: "yearsOfProfessionalExperience",
        label: "Years of Professional Experience",
        required: true,
        type: "select",
        options: [
            {label: "0", value: "0"},
            {label: "1", value: "1"},
            {label: "2", value: "2"},
            {label: "3", value: "3"},
            {label: "4", value: "4"},
            {label: "5", value: "5"},
            {label: "6", value: "6"},
            {label: "7", value: "7"},
            {label: "8", value: "8"},
            {label: "9", value: "9"},
            {label: "10+", value: "10+"},
        ],
        answer: "",
    },
};

const workAuthorizationMap = {
    STAFFAUG: [
        {label: "US Citizen", value: "US Citizen"},
        {label: "Permanent Resident", value: "Permanent Resident"},
        {label: "DACA", value: "DACA"},
        {label: "H-1B", value: "H-1B"},
        {label: "GC/EAD", value: "GC/EAD"},
        {label: "OPT/EAD", value: "OPT/EAD"},
        {label: "EAD", value: "EAD"},
        {label: "H-4/EAD", value: "H-4/EAD"},
        {label: "TN Visa", value: "TN Visa"},
        {label: "Other", value: "Other"},
        {label: "No", value: "Not Authorized"},
    ],
    HTD: [
        {label: "US Citizen", value: "US Citizen"},
        {label: "Permanent Resident", value: "Permanent Resident"},
        {label: "DACA", value: "DACA"},
        {label: "H-1B", value: "H-1B"},
        {label: "OPT/EAD", value: "OPT/EAD"},
        {label: "EAD", value: "EAD"},
        {label: "H-4/EAD", value: "H-4/EAD"},
        {label: "Other", value: "Other"},
        {label: "No", value: "Not Authorized"},
    ],
};

export const getInitialQuestions = (corpType: "HTD" | "STAFFAUG" = "HTD"): QuestionType[] => {
    switch (corpType) {
        case "HTD":
            return [
                questionList.firstName,
                questionList.lastName,
                questionList.nickName,
                questionList.email,
                questionList.phone,
                questionList.zip,
                // questionList.city,
                // questionList.state,
                {...questionList.workAuthorization, options: workAuthorizationMap.HTD},
                questionList.relocation,
                questionList.codingAbility,
                questionList.techSelection,
                // questionList.strongestLanguageOther,
                questionList.yearsOfExperience,
                questionList.currentlyStudent,
                // questionList.graduationMonth,
                // questionList.graduationYear,
                // questionList.degreeExpected,
                questionList.highestDegree,
                // questionList.major,
                questionList.linkedin,
                questionList.voluntaryDisclosures,
                questionList.gender,
                questionList.disability,
                questionList.race,
                questionList.isMilitary,
                questionList.militaryStatus,
                questionList.militaryBranch,
                // questionList.resume,
            ];
        case "STAFFAUG":
            return [
                questionList.firstName,
                questionList.lastName,
                questionList.nickName,
                questionList.email,
                questionList.phone,
                questionList.zip,
                questionList.city,
                questionList.state,
                {...questionList.workAuthorization, options: workAuthorizationMap.STAFFAUG},
                questionList.willRelocate,
                {...questionList.highestDegree, required: true, displayRule: undefined, displayMultiRules: undefined},
                questionList.yearsOfProfessionalExperience,
                questionList.resume,
            ];
    }
};
